import { getLocalStorage } from "@elit-hair/elit-tools";
import { t } from "i18next";
import i18n from "i18next";
import { DateTime } from "luxon";

const getStoredDateFormat = (): string => {
    return getLocalStorage("preferredDateFormat", "dd.MM.yyyy");
};

export const formatDate = (date: string): string => {
    const locale = i18n.language;
    const storedFormat = getStoredDateFormat();
    return DateTime.fromISO(date).setLocale(locale).toFormat(storedFormat);
};

export const formatDateWithDayName = (date: string): string => {
    const locale = i18n.language;
    const storedFormat = getStoredDateFormat();
    const dateTime = DateTime.fromISO(date).setLocale(locale);
    const formattedDate = dateTime.toFormat(storedFormat);
    const dayName = dateTime.toFormat("EEEE");
    return `${formattedDate} ${dayName}`;
};

export const formatDateRelative = (date: string): string => {
    const locale = i18n.language;
    const dateTime = DateTime.fromISO(date).setLocale(locale);
    const now = DateTime.now().setLocale(locale);
    const diffInDays = now.diff(dateTime, "days").days;
    const fullDate = dateTime.toFormat(getStoredDateFormat());

    if (diffInDays === 0) {
        return `${t("today")} • ${fullDate}`;
    } else if (diffInDays === 1) {
        return `${t("yesterday")} • ${fullDate}`;
    } else if (diffInDays < 7) {
        const weekdayKey = dateTime.setLocale("en").toFormat("EEEE").toLowerCase();
        const dayName = t(`weekdays.${weekdayKey}`);
        return `${dayName} • ${fullDate}`;
    } else {
        return fullDate;
    }
};

export const getDayKey = (date: string): string => {
    const locale = i18n.language;
    return DateTime.fromISO(date).setLocale(locale).toFormat(getStoredDateFormat());
};

export const getDateString = (date: DateTime): string => date.toISODate() ?? "";

export const getLastNDates = (endDate: string, n: number): string[] => {
    const result: string[] = [];
    const end = DateTime.fromISO(endDate);
    if (!end.isValid) return result;

    for (let i = 0; i < n; i++) {
        const d = end.minus({ days: i });
        result.push(getDateString(d));
    }
    return result.reverse();
};

export const getDatesInMonth = (month: DateTime): string[] => {
    if (!month.isValid) return [];

    const start = month.startOf("month");
    const daysInMonth = start.daysInMonth ?? 0;
    const result: string[] = [];

    for (let day = 0; day < daysInMonth; day++) {
        result.push(getDateString(start.plus({ days: day })));
    }

    return result;
};

export const convertToDateRange = (startDate?: string, endDate?: string) => {
    return startDate || endDate
        ? {
              from: startDate ? new Date(startDate) : undefined,
              to: endDate ? new Date(endDate) : undefined,
          }
        : undefined;
};

export const convertFromDateRange = (range: { from?: Date; to?: Date } | undefined) => {
    const startDate = range?.from ? (DateTime.fromJSDate(range.from).toISODate() ?? undefined) : undefined;
    const endDate = range?.to ? (DateTime.fromJSDate(range.to).toISODate() ?? undefined) : undefined;

    return { startDate, endDate };
};
